/**
 * Per-country destination data (fallback content).
 * Extracted from DestinationPage.jsx as part of Phase D2 tech-debt cleanup.
 *
 * The CMS can override any field of this object per-country via the
 * `destination-{country}` page slug — see DestinationPage.jsx's
 * deepMergeContent(fallback, cmsOverride) call.
 *
 * Keys are URL-friendly country slugs: usa, uk, canada, australia,
 * germany, new-zealand, dubai, france, singapore, malaysia, ireland.
 */

const destinationData = {
  usa: {
    name: 'USA',
    fullName: 'United States of America',
    flag: '🇺🇸',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/8987d0699afe7402f73b16854767f98f32ebfd00a18dfe847a37444db5353bd5.png',
    heroImageMobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/9d3e17aa2020c83ccc95868fe39e725492ff1e399754fa2ff8e7afb2325083c1.png',
    tagline: 'Home to World\'s Top Universities',
    description: 'The United States remains the most popular destination for international students, offering world-class education, diverse programs, and exceptional career opportunities.',
    stats: {
      universities: '4000+',
      internationalStudents: '1M+',
      avgTuition: '$20,000 - $60,000/year',
      workVisa: 'Up to 3 Years OPT (STEM)',
      intakes: 'Fall, Spring, Summer'
    },
    whyStudy: [
      { title: 'World-Class Universities', description: 'Home to 4 of the top 10 global universities in the QS 2027 rankings (MIT #1, Stanford #2, Harvard #5, Caltech #7), and 8 of the top 10 by ARWU/Shanghai methodology' },
      { title: 'Diverse Programs', description: 'Thousands of programs across every field imaginable with flexible credit systems' },
      { title: 'Research Opportunities', description: 'Leading destination for cutting-edge research and innovation' },
      { title: 'Career Prospects', description: '12-month OPT + 24-month STEM extension (36 months total) for qualified graduates' },
      { title: 'Campus Life', description: 'Vibrant campus culture, extensive facilities, and diverse student communities' },
      { title: 'Global Network', description: 'Build connections with students from 200+ countries' }
    ],
    topUniversities: [
      { 
        id: 'mit',
        name: 'Massachusetts Institute of Technology', 
        shortName: 'MIT',
        rank: 1, 
        location: 'Cambridge, MA', 
        programs: ['Engineering', 'Computer Science', 'Business'],
        tuition: '$57,590',
        acceptanceRate: '4%',
        avgSalary: '$115,000',
        studentCount: '11,500',
        intlStudents: '33%',
        duration: '2 years (MS)'
      },
      { 
        id: 'stanford',
        name: 'Stanford University', 
        shortName: 'Stanford',
        rank: 2, 
        location: 'Stanford, CA', 
        programs: ['Business', 'Engineering', 'Medicine'],
        tuition: '$56,169',
        acceptanceRate: '4%',
        avgSalary: '$120,000',
        studentCount: '17,500',
        intlStudents: '24%',
        duration: '2 years (MS)'
      },
      { 
        id: 'harvard',
        name: 'Harvard University', 
        shortName: 'Harvard',
        rank: 5, 
        location: 'Cambridge, MA', 
        programs: ['Law', 'Business', 'Medicine'],
        tuition: '$54,768',
        acceptanceRate: '3%',
        avgSalary: '$125,000',
        studentCount: '23,700',
        intlStudents: '26%',
        duration: '2 years (MS)'
      },
      { 
        id: 'caltech',
        name: 'California Institute of Technology', 
        shortName: 'Caltech',
        rank: 7, 
        location: 'Pasadena, CA', 
        programs: ['Science', 'Engineering', 'Physics'],
        tuition: '$58,680',
        acceptanceRate: '3%',
        avgSalary: '$110,000',
        studentCount: '2,200',
        intlStudents: '30%',
        duration: '2 years (MS)'
      },
      { 
        id: 'uchicago',
        name: 'University of Chicago', 
        shortName: 'UChicago',
        rank: 24, 
        location: 'Chicago, IL', 
        programs: ['Economics', 'Business', 'Law'],
        tuition: '$60,552',
        acceptanceRate: '5%',
        avgSalary: '$105,000',
        studentCount: '17,800',
        intlStudents: '28%',
        duration: '2 years (MS)'
      },
      { 
        id: 'columbia',
        name: 'Columbia University', 
        shortName: 'Columbia',
        rank: 43, 
        location: 'New York, NY', 
        programs: ['Journalism', 'Business', 'Law'],
        tuition: '$63,530',
        acceptanceRate: '4%',
        avgSalary: '$108,000',
        studentCount: '33,400',
        intlStudents: '35%',
        duration: '2 years (MS)'
      },
    ],
    popularCourses: ['Computer Science & IT', 'Business & MBA', 'Engineering', 'Data Science & AI', 'Healthcare & Medicine', 'Finance & Economics'],
    costs: { tuitionRange: '$20,000 - $60,000', livingCost: '$15,000 - $25,000', totalAnnual: '$35,000 - $85,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Minimum 3.0 GPA preferred'],
      tests: ['TOEFL (90+) or IELTS (6.5+)', 'GRE/GMAT for graduate programs'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume']
    },
    scholarships: [
      { name: 'Fulbright Scholarship', coverage: 'Full tuition + living', eligibility: 'Merit-based' },
      { name: 'University Merit Scholarships', coverage: '$10,000 - $50,000', eligibility: 'Academic excellence' },
      { name: 'Graduate Assistantships', coverage: 'Tuition waiver + stipend', eligibility: 'Research/Teaching' }
    ],
    timeline: [
      { month: 'Jan-Mar', activity: 'Research universities, prepare for tests' },
      { month: 'Apr-Jun', activity: 'Take GRE/GMAT, finalize shortlist' },
      { month: 'Jul-Sep', activity: 'Prepare documents, write SOPs' },
      { month: 'Oct-Dec', activity: 'Submit applications' },
      { month: 'Jan-Apr', activity: 'Receive decisions, accept offers' },
      { month: 'May-Jul', activity: 'Visa process, pre-departure prep' }
    ],
    intakes: [
      {
        name: 'Jan-Mar',
        startMonth: 'Apr',
        endMonth: 'Dec',
        steps: [
          { month: 'Apr-May', activity: 'Research Universities', details: 'Explore programs, compare universities, register for GRE/GMAT' },
          { month: 'May-Jun', activity: 'Take GRE/GMAT & TOEFL', details: 'Complete standardized tests, finalize shortlist' },
          { month: 'Jun-Jul', activity: 'Prepare Documents', details: 'Write SOPs, get LORs, prepare transcripts' },
          { month: 'Aug-Sep', activity: 'Submit Applications', details: 'Apply to universities, pay application fees' },
          { month: 'Sep-Oct', activity: 'Receive Decisions', details: 'Get admission decisions, accept offer, pay deposit' },
          { month: 'Nov-Dec', activity: 'Visa & Travel', details: 'Apply for F-1 visa, book flights, arrange housing' }
        ]
      },
      {
        name: 'Aug-Sep',
        startMonth: 'Jan',
        endMonth: 'Jul',
        steps: [
          { month: 'Jan-Mar', activity: 'Research Universities', details: 'Explore programs with fall intake, prepare for tests' },
          { month: 'Apr-Jun', activity: 'Take GRE/GMAT & TOEFL', details: 'Complete standardized tests, finalize shortlist' },
          { month: 'Jul-Sep', activity: 'Prepare Documents', details: 'Write SOPs, get LORs, prepare transcripts' },
          { month: 'Oct-Jan', activity: 'Submit Applications', details: 'Apply to universities with fall deadlines' },
          { month: 'Feb-May', activity: 'Receive Decisions', details: 'Get admission decisions, accept offer' },
          { month: 'May-Jul', activity: 'Visa & Travel', details: 'Apply for F-1 visa, prepare for August start' }
        ]
      }
    ]
  },
  uk: {
    name: 'UK',
    fullName: 'United Kingdom',
    flag: '🇬🇧',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/6c8de25df10e898a9d3600b71e27425f9b4c560944b630df0b6c5e460794504a.png',
    heroImageMobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/ca684d55cf4628fb3f9db6bb8f4546a7be593055382f81f0300f4a9364d38a5d.png',
    tagline: 'Historic Excellence in Education',
    description: 'The UK offers prestigious universities with centuries of academic tradition, shorter program durations, and excellent post-study work opportunities.',
    stats: {
      universities: '150+',
      internationalStudents: '500K+',
      avgTuition: '£15,000 - £35,000/year',
      workVisa: '2 Years Graduate Visa',
      intakes: 'September, January'
    },
    whyStudy: [
      { title: 'World-Renowned Universities', description: 'Home to Oxford, Cambridge, and other globally recognized institutions' },
      { title: 'Shorter Duration', description: '1-year Masters programs save time and money compared to other countries' },
      { title: 'Graduate Route Visa', description: '2-year post-study work visa for Bachelor\'s/Master\'s (3 years for PhD). Note: for applications from Jan 1, 2027, non-PhD duration drops to 18 months.' },
      { title: 'Rich Cultural Experience', description: 'Historic cities, diverse culture, and easy access to Europe' },
      { title: 'Quality Education', description: 'Rigorous academic standards and globally recognized qualifications' },
      { title: 'Research Excellence', description: 'World-leading research across all disciplines' }
    ],
    topUniversities: [
      { 
        id: 'oxford',
        name: 'University of Oxford', 
        shortName: 'Oxford',
        rank: 4, 
        location: 'Oxford', 
        programs: ['Law', 'Medicine', 'Humanities'],
        tuition: '£32,000',
        acceptanceRate: '15%',
        avgSalary: '£55,000',
        studentCount: '26,000',
        intlStudents: '45%',
        duration: '1 year (MS)'
      },
      { 
        id: 'cambridge',
        name: 'University of Cambridge', 
        shortName: 'Cambridge',
        rank: 6, 
        location: 'Cambridge', 
        programs: ['Science', 'Engineering', 'Economics'],
        tuition: '£33,825',
        acceptanceRate: '18%',
        avgSalary: '£52,000',
        studentCount: '24,500',
        intlStudents: '40%',
        duration: '1 year (MS)'
      },
      { 
        id: 'imperial',
        name: 'Imperial College London', 
        shortName: 'Imperial',
        rank: 2, 
        location: 'London', 
        programs: ['Engineering', 'Science', 'Business'],
        tuition: '£35,100',
        acceptanceRate: '14%',
        avgSalary: '£58,000',
        studentCount: '20,000',
        intlStudents: '60%',
        duration: '1 year (MS)'
      },
      { 
        id: 'ucl',
        name: 'UCL', 
        shortName: 'UCL',
        rank: 8, 
        location: 'London', 
        programs: ['Architecture', 'Law', 'Education'],
        tuition: '£30,800',
        acceptanceRate: '16%',
        avgSalary: '£48,000',
        studentCount: '46,000',
        intlStudents: '55%',
        duration: '1 year (MS)'
      },
      { 
        id: 'edinburgh',
        name: 'University of Edinburgh', 
        shortName: 'Edinburgh',
        rank: 35, 
        location: 'Edinburgh', 
        programs: ['AI', 'Medicine', 'Law'],
        tuition: '£28,950',
        acceptanceRate: '10%',
        avgSalary: '£45,000',
        studentCount: '35,000',
        intlStudents: '45%',
        duration: '1 year (MS)'
      },
      { 
        id: 'kcl',
        name: 'King\'s College London', 
        shortName: 'KCL',
        rank: 37, 
        location: 'London', 
        programs: ['Law', 'Medicine', 'Humanities'],
        tuition: '£26,550',
        acceptanceRate: '13%',
        avgSalary: '£42,000',
        studentCount: '35,000',
        intlStudents: '50%',
        duration: '1 year (MS)'
      },
    ],
    popularCourses: ['Business & Management', 'Engineering', 'Law', 'Computer Science', 'Medicine', 'Arts & Design'],
    costs: { tuitionRange: '£15,000 - £35,000', livingCost: '£12,000 - £18,000', totalAnnual: '£27,000 - £53,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Minimum 2:1 or equivalent'],
      tests: ['IELTS (6.5+) or equivalent'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume']
    },
    scholarships: [
      { name: 'Chevening Scholarship', coverage: 'Full tuition + living', eligibility: 'Leadership potential' },
      { name: 'Commonwealth Scholarship', coverage: 'Full funding', eligibility: 'Commonwealth countries' },
      { name: 'University Scholarships', coverage: '£5,000 - £20,000', eligibility: 'Merit-based' }
    ],
    timeline: [
      { month: 'Jan-Mar', activity: 'Research universities, IELTS prep' },
      { month: 'Apr-Jun', activity: 'Take IELTS, finalize shortlist' },
      { month: 'Jul-Sep', activity: 'Prepare and submit applications' },
      { month: 'Oct-Dec', activity: 'Receive offers, apply for scholarships' },
      { month: 'Jan-Apr', activity: 'Accept offer, arrange finances' },
      { month: 'May-Aug', activity: 'Visa process, travel arrangements' }
    ],
    intakes: [
      {
        name: 'Jan',
        startMonth: 'Mar',
        endMonth: 'Dec',
        steps: [
          { month: 'Mar-Apr', activity: 'Research Universities', details: 'Explore programs, compare universities, prepare for IELTS' },
          { month: 'May-Jun', activity: 'Take IELTS', details: 'Complete IELTS test, finalize university shortlist' },
          { month: 'Aug', activity: 'Submit Applications', details: 'Apply via UCAS or directly, prepare documents' },
          { month: 'Sep', activity: 'Receive Offers', details: 'Get conditional/unconditional offers, accept CAS' },
          { month: 'Oct', activity: 'Apply for Visa', details: 'Submit Student visa (Student Route) application' },
          { month: 'Nov-Dec', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Sep-Oct',
        startMonth: 'Feb',
        endMonth: 'Aug',
        steps: [
          { month: 'Feb-Mar', activity: 'Research Universities', details: 'Explore programs with September intake' },
          { month: 'Apr-Jun', activity: 'Take IELTS', details: 'Complete IELTS test, finalize shortlist' },
          { month: 'Jul-Sep', activity: 'Submit Applications', details: 'Apply to universities with September start' },
          { month: 'Oct-Dec', activity: 'Receive Offers', details: 'Get offers, accept CAS' },
          { month: 'Jan-Apr', activity: 'Apply for Visa', details: 'Submit student visa application' },
          { month: 'May-Aug', activity: 'Travel Prep', details: 'Visa approval, arrange travel' }
        ]
      }
    ]
  },
  canada: {
    name: 'Canada',
    fullName: 'Canada',
    flag: '🇨🇦',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/c1aa510683c445460131840d513e4f553c79a26742a00f3a471d4fab7e8793a2.png',
    heroImageMobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/68d975916fd10fe4a39c86cb711911ba23c1c7e4b2d2363f79785f950106419c.png',
    tagline: 'Quality Education with Immigration Pathway',
    description: 'Canada combines excellent education with welcoming immigration policies, though recent 2025 updates have introduced field-of-study and language requirements for PGWP eligibility.',
    stats: {
      universities: '100+',
      internationalStudents: '800K+',
      avgTuition: 'CAD 15,000 - 35,000/year',
      workVisa: 'Up to 3 Years PGWP',
      intakes: 'Fall, Winter, Summer'
    },
    whyStudy: [
      { title: 'PR Pathway', description: 'Clear path to permanent residency through Express Entry and PNP programs' },
      { title: 'Affordable Education', description: 'Lower tuition compared to US and UK with high-quality education' },
      { title: 'Work Opportunities', description: 'PGWP up to 3 years (field-of-study requirements apply for some programs)' },
      { title: 'Safe & Welcoming', description: 'One of the safest and most multicultural countries in the world' },
      { title: 'Quality of Life', description: 'Excellent healthcare, beautiful landscapes, and high living standards' },
      { title: 'Bilingual Advantage', description: 'Learn in English or French, boosting career opportunities' }
    ],
    topUniversities: [
      { 
        id: 'utoronto',
        name: 'University of Toronto', 
        shortName: 'UofT',
        rank: 32, 
        location: 'Toronto, ON', 
        programs: ['Business', 'Engineering', 'Medicine'],
        tuition: 'CAD 58,680',
        acceptanceRate: '43%',
        avgSalary: 'CAD 85,000',
        studentCount: '97,000',
        intlStudents: '25%',
        duration: '2 years (MS)'
      },
      { 
        id: 'mcgill',
        name: 'McGill University', 
        shortName: 'McGill',
        rank: 30, 
        location: 'Montreal, QC', 
        programs: ['Law', 'Medicine', 'Arts'],
        tuition: 'CAD 24,930',
        acceptanceRate: '42%',
        avgSalary: 'CAD 78,000',
        studentCount: '40,000',
        intlStudents: '30%',
        duration: '2 years (MS)'
      },
      { 
        id: 'ubc',
        name: 'University of British Columbia', 
        shortName: 'UBC',
        rank: 45, 
        location: 'Vancouver, BC', 
        programs: ['Science', 'Business', 'Engineering'],
        tuition: 'CAD 42,803',
        acceptanceRate: '52%',
        avgSalary: 'CAD 82,000',
        studentCount: '70,000',
        intlStudents: '28%',
        duration: '2 years (MS)'
      },
      { 
        id: 'ualberta',
        name: 'University of Alberta', 
        shortName: 'UAlberta',
        rank: 96, 
        location: 'Edmonton, AB', 
        programs: ['Engineering', 'Science', 'Business'],
        tuition: 'CAD 32,400',
        acceptanceRate: '58%',
        avgSalary: 'CAD 75,000',
        studentCount: '40,000',
        intlStudents: '22%',
        duration: '2 years (MS)'
      },
      { 
        id: 'uwaterloo',
        name: 'University of Waterloo', 
        shortName: 'Waterloo',
        rank: 113, 
        location: 'Waterloo, ON', 
        programs: ['Computer Science', 'Engineering', 'Math'],
        tuition: 'CAD 36,500',
        acceptanceRate: '53%',
        avgSalary: 'CAD 90,000',
        studentCount: '42,000',
        intlStudents: '24%',
        duration: '2 years (MS)'
      },
      { 
        id: 'umontreal',
        name: 'University of Montreal', 
        shortName: 'UdeM',
        rank: 162, 
        location: 'Montreal, QC', 
        programs: ['AI', 'Law', 'Medicine'],
        tuition: 'CAD 25,300',
        acceptanceRate: '57%',
        avgSalary: 'CAD 72,000',
        studentCount: '67,000',
        intlStudents: '20%',
        duration: '2 years (MS)'
      },
    ],
    popularCourses: ['Computer Science & IT', 'Business Analytics', 'Engineering', 'Healthcare', 'Data Science', 'Environmental Science'],
    costs: { tuitionRange: 'CAD 15,000 - 35,000', livingCost: 'CAD 12,000 - 18,000', totalAnnual: 'CAD 27,000 - 53,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Minimum 3.0 GPA', 'Program in eligible CIP code (for PGWP)'],
      tests: ['IELTS (6.5+) or TOEFL (90+) - CLB 7 for university, CLB 5 for college', 'GRE for some programs'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume', 'Language proficiency proof']
    },
    scholarships: [
      { name: 'Vanier CGS', coverage: 'CAD 50,000/year', eligibility: 'Doctoral students' },
      { name: 'University Scholarships', coverage: 'CAD 5,000 - 25,000', eligibility: 'Merit-based' },
      { name: 'Provincial Scholarships', coverage: 'Varies', eligibility: 'Province-specific' }
    ],
    timeline: [
      { month: 'Jan-Mar', activity: 'Research programs, IELTS prep' },
      { month: 'Apr-Jun', activity: 'Take tests, finalize shortlist' },
      { month: 'Jul-Sep', activity: 'Submit applications' },
      { month: 'Oct-Dec', activity: 'Receive offers' },
      { month: 'Jan-Apr', activity: 'Accept offer, apply for study permit' },
      { month: 'May-Aug', activity: 'Visa approval, travel prep' }
    ],
    intakes: [
      {
        name: 'Jan-Mar',
        startMonth: 'Dec',
        endMonth: 'Dec',
        steps: [
          { month: 'Dec-Jan', activity: 'Research Universities', details: 'Explore programs, compare universities, prepare for IELTS' },
          { month: 'Jan-Feb', activity: 'Take IELTS', details: 'Complete IELTS test, finalize university shortlist' },
          { month: 'Feb-Mar', activity: 'Submit Applications', details: 'Apply to universities, prepare documents' },
          { month: 'Apr-Jul', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'Sep-Oct', activity: 'Apply for Study Permit', details: 'Submit study permit application' },
          { month: 'Nov-Dec', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Aug-Sep',
        startMonth: 'Sep',
        endMonth: 'Aug',
        steps: [
          { month: 'Sep-Oct', activity: 'Research Universities', details: 'Explore programs with fall intake' },
          { month: 'Oct-Nov', activity: 'Take IELTS', details: 'Complete IELTS test, finalize shortlist' },
          { month: 'Nov-Jan', activity: 'Submit Applications', details: 'Apply to universities with fall deadlines' },
          { month: 'Mar-May', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'May-Jun', activity: 'Apply for Study Permit', details: 'Submit study permit application' },
          { month: 'Jul-Aug', activity: 'Travel Prep', details: 'Visa approval, arrange travel' }
        ]
      }
    ]
  },
  australia: {
    name: 'Australia',
    fullName: 'Australia',
    flag: '🇦🇺',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/485a7f5ddb2e7edd0dac78f0933d7e23f2ee733e4c56470188d7442e87381ff0.png',
    heroImageMobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/bc2ae005bb759df373d79cae64c1304cc4d5231f2891574d0cec22292321d97a.png',
    tagline: 'World-Class Education in Paradise',
    description: 'Australia offers high-quality education with a laid-back lifestyle and stunning natural beauty. Recent 2025 visa changes have updated post-study work rules including age limits and English requirements.',
    stats: {
      universities: '43',
      internationalStudents: '750K+',
      avgTuition: 'AUD 25,000 - 45,000/year',
      workVisa: '2-3 Years (485 Visa)',
      intakes: 'February, July'
    },
    whyStudy: [
      { title: 'Group of Eight', description: 'Home to 8 world-leading research universities' },
      { title: 'Work While Studying', description: '48 hours per fortnight during term, unlimited during breaks' },
      { title: 'Post-Study Work', description: '485 Visa: 2 years (Bachelor\'s/Master\'s), 3 years (PhD). Age limit under 35' },
      { title: 'Quality of Life', description: 'Consistently ranked among the world\'s most liveable countries — Melbourne, Sydney and Adelaide feature in the EIU top 10 most-liveable cities year on year' },
      { title: 'Diverse Culture', description: 'Welcoming multicultural society with vibrant communities' },
      { title: 'Natural Beauty', description: 'Stunning beaches, wildlife, and outdoor lifestyle' }
    ],
    topUniversities: [
      { 
        id: 'melbourne',
        name: 'University of Melbourne', 
        shortName: 'Melbourne',
        rank: 22, 
        location: 'Melbourne, VIC', 
        programs: ['Law', 'Medicine', 'Business'],
        tuition: 'AUD 47,000',
        acceptanceRate: '70%',
        avgSalary: 'AUD 95,000',
        studentCount: '65,000',
        intlStudents: '42%',
        duration: '2 years (MS)'
      },
      { 
        id: 'sydney',
        name: 'University of Sydney', 
        shortName: 'Sydney',
        rank: 19, 
        location: 'Sydney, NSW', 
        programs: ['Engineering', 'Business', 'Architecture'],
        tuition: 'AUD 50,000',
        acceptanceRate: '68%',
        avgSalary: 'AUD 90,000',
        studentCount: '73,000',
        intlStudents: '40%',
        duration: '2 years (MS)'
      },
      { 
        id: 'unsw',
        name: 'UNSW Sydney', 
        shortName: 'UNSW',
        rank: 19, 
        location: 'Sydney, NSW', 
        programs: ['Engineering', 'Business', 'Science'],
        tuition: 'AUD 48,000',
        acceptanceRate: '72%',
        avgSalary: 'AUD 88,000',
        studentCount: '64,000',
        intlStudents: '38%',
        duration: '2 years (MS)'
      },
      { 
        id: 'anu',
        name: 'Australian National University', 
        shortName: 'ANU',
        rank: 34, 
        location: 'Canberra, ACT', 
        programs: ['Politics', 'Science', 'Engineering'],
        tuition: 'AUD 47,880',
        acceptanceRate: '35%',
        avgSalary: 'AUD 85,000',
        studentCount: '25,500',
        intlStudents: '44%',
        duration: '2 years (MS)'
      },
      { 
        id: 'monash',
        name: 'Monash University', 
        shortName: 'Monash',
        rank: 42, 
        location: 'Melbourne, VIC', 
        programs: ['Pharmacy', 'Engineering', 'Business'],
        tuition: 'AUD 45,800',
        acceptanceRate: '74%',
        avgSalary: 'AUD 82,000',
        studentCount: '86,000',
        intlStudents: '35%',
        duration: '2 years (MS)'
      },
      { 
        id: 'uq',
        name: 'University of Queensland', 
        shortName: 'UQ',
        rank: 43, 
        location: 'Brisbane, QLD', 
        programs: ['Science', 'Engineering', 'Agriculture'],
        tuition: 'AUD 43,500',
        acceptanceRate: '75%',
        avgSalary: 'AUD 80,000',
        studentCount: '55,000',
        intlStudents: '32%',
        duration: '2 years (MS)'
      },
    ],
    popularCourses: ['Business & Management', 'Engineering', 'IT & Computer Science', 'Healthcare & Nursing', 'Hospitality', 'Environmental Science'],
    costs: { tuitionRange: 'AUD 25,000 - 45,000', livingCost: 'AUD 21,000 - 28,000', totalAnnual: 'AUD 46,000 - 73,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Minimum 60-65% marks'],
      tests: ['IELTS 6.5+ (no band below 5.5) or PTE 58+ (no score below 50)', 'Test must be less than 12 months old'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume', 'GTE Statement', 'Age under 35 for 485 visa']
    },
    scholarships: [
      { name: 'Australia Awards', coverage: 'Full tuition + living', eligibility: 'Developing countries' },
      { name: 'Destination Australia', coverage: 'AUD 15,000/year', eligibility: 'Regional universities' },
      { name: 'University Scholarships', coverage: '10-50% tuition', eligibility: 'Academic merit' }
    ],
    timeline: [
      { month: 'Mar-May', activity: 'Research universities, IELTS prep' },
      { month: 'Jun-Aug', activity: 'Take IELTS, finalize shortlist' },
      { month: 'Sep-Nov', activity: 'Submit applications for Feb intake' },
      { month: 'Dec-Jan', activity: 'Receive offers, accept CoE' },
      { month: 'Feb-Mar', activity: 'Apply for student visa' },
      { month: 'Apr-Jun', activity: 'Visa approval, travel prep' }
    ],
    intakes: [
      {
        name: 'Feb-Mar',
        startMonth: 'Mar',
        endMonth: 'Feb',
        steps: [
          { month: 'Mar-Apr', activity: 'Research Universities', details: 'Explore courses, compare universities, prepare for IELTS/PTE' },
          { month: 'May-Jun', activity: 'Take IELTS/PTE', details: 'Complete English test, finalize university shortlist' },
          { month: 'Jul-Aug', activity: 'Submit Applications', details: 'Apply to universities, pay application fees' },
          { month: 'Sep-Nov', activity: 'Receive Offers', details: 'Get offer letters, GS clearance, accept COE' },
          { month: 'Nov-Dec', activity: 'Apply for Visa', details: 'Submit student visa (subclass 500) application' },
          { month: 'Jan-Feb', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Jul',
        startMonth: 'Oct',
        endMonth: 'Jul',
        steps: [
          { month: 'Oct-Nov', activity: 'Research Universities', details: 'Explore courses, compare universities, prepare for IELTS/PTE' },
          { month: 'Dec-Jan', activity: 'Take IELTS/PTE', details: 'Complete English test, finalize university shortlist' },
          { month: 'Feb-Mar', activity: 'Submit Applications', details: 'Apply to universities, pay application fees' },
          { month: 'Mar-Apr', activity: 'Receive Offers', details: 'Get offer letters, GS clearance, accept COE' },
          { month: 'Apr-May', activity: 'Apply for Visa', details: 'Submit student visa (subclass 500) application' },
          { month: 'Jun-Jul', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Nov',
        startMonth: 'May',
        endMonth: 'Nov',
        steps: [
          { month: 'May-Jun', activity: 'Research Universities', details: 'Explore courses, compare universities, prepare for IELTS/PTE' },
          { month: 'Jun-Jul', activity: 'Take IELTS/PTE', details: 'Complete English test, finalize university shortlist' },
          { month: 'Jul-Aug', activity: 'Submit Applications', details: 'Apply to universities, pay application fees' },
          { month: 'Aug-Sep', activity: 'Receive Offers', details: 'Get offer letters, GS clearance, accept COE' },
          { month: 'Sep-Oct', activity: 'Apply for Visa', details: 'Submit student visa (subclass 500) application' },
          { month: 'Oct-Nov', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      }
    ]
  },
  germany: {
    name: 'Germany',
    fullName: 'Germany',
    flag: '🇩🇪',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4819bad9a28616f54a185f58df90b442c70b8243a1b20006c59ea6650ecd722a.png',
    heroImageMobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/be205fce2fa0d62a57f0ca393efc9654c3236b456a5bb7967b83eba7ecc8ebb4.png',
    tagline: 'Free World-Class Education',
    description: 'Germany offers tuition-free or low-cost education at world-renowned technical universities, combined with excellent research facilities and strong industry connections.',
    stats: {
      universities: '400+',
      internationalStudents: '400K+',
      avgTuition: '€0 - 20,000/year',
      workVisa: '18 Month Job Seeker',
      intakes: 'Winter (Oct), Summer (Apr)'
    },
    whyStudy: [
      { title: 'Low or No Tuition', description: 'Most public universities charge €0-€3,000/yr; Bavaria (TU Munich, LMU) reintroduced ~€2,000-3,000/semester for non-EU students from WS 2024/25' },
      { title: 'Engineering Excellence', description: 'World-famous for engineering and technical education' },
      { title: 'Industry Connections', description: 'Strong ties with companies like BMW, Siemens, Bosch' },
      { title: 'Research Hub', description: 'Major funding for research and development' },
      { title: 'Job Seeker Visa', description: '18-month visa to find a job after graduation' },
      { title: 'Central Location', description: 'Travel easily across Europe' }
    ],
    topUniversities: [
      { 
        id: 'tumunich',
        name: 'Technical University of Munich', 
        shortName: 'TU Munich',
        rank: 37, 
        location: 'Munich', 
        programs: ['Engineering', 'Computer Science', 'Physics'],
        tuition: '€4,000-6,000/yr',
        acceptanceRate: '8%',
        avgSalary: '€65,000',
        studentCount: '50,000',
        intlStudents: '35%',
        duration: '2 years (MS)'
      },
      { 
        id: 'lmu',
        name: 'LMU Munich', 
        shortName: 'LMU',
        rank: 59, 
        location: 'Munich', 
        programs: ['Science', 'Law', 'Economics'],
        tuition: '€4,000-6,000/yr',
        acceptanceRate: '12%',
        avgSalary: '€58,000',
        studentCount: '52,000',
        intlStudents: '18%',
        duration: '2 years (MS)'
      },
      { 
        id: 'heidelberg',
        name: 'Heidelberg University', 
        shortName: 'Heidelberg',
        rank: 49, 
        location: 'Heidelberg', 
        programs: ['Medicine', 'Law', 'Philosophy'],
        tuition: '€3,000/yr',
        acceptanceRate: '15%',
        avgSalary: '€55,000',
        studentCount: '30,000',
        intlStudents: '20%',
        duration: '2 years (MS)'
      },
      { 
        id: 'rwth',
        name: 'RWTH Aachen University', 
        shortName: 'RWTH Aachen',
        rank: 90, 
        location: 'Aachen', 
        programs: ['Engineering', 'Computer Science', 'Science'],
        tuition: '€0',
        acceptanceRate: '25%',
        avgSalary: '€62,000',
        studentCount: '47,000',
        intlStudents: '22%',
        duration: '2 years (MS)'
      },
      { 
        id: 'tuberlin',
        name: 'TU Berlin', 
        shortName: 'TU Berlin',
        rank: 106, 
        location: 'Berlin', 
        programs: ['Engineering', 'Architecture', 'Science'],
        tuition: '€0',
        acceptanceRate: '30%',
        avgSalary: '€58,000',
        studentCount: '35,000',
        intlStudents: '25%',
        duration: '2 years (MS)'
      },
      { 
        id: 'kit',
        name: 'Karlsruhe Institute of Technology', 
        shortName: 'KIT',
        rank: 119, 
        location: 'Karlsruhe', 
        programs: ['Engineering', 'IT', 'Science'],
        tuition: '€3,000/yr',
        acceptanceRate: '28%',
        avgSalary: '€60,000',
        studentCount: '25,000',
        intlStudents: '20%',
        duration: '2 years (MS)'
      },
    ],
    popularCourses: ['Mechanical Engineering', 'Computer Science', 'Automotive Engineering', 'Business Administration', 'Data Science', 'Renewable Energy'],
    costs: { tuitionRange: '€0 - 6,000/yr (state-dependent for non-EU)', livingCost: '€10,000 - 12,000', totalAnnual: '€10,000 - 18,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Good academic record'],
      tests: ['German (B2/C1) or English (IELTS 6.5+)', 'GRE for some programs'],
      documents: ['Transcripts', 'Motivation Letter', 'LORs', 'APS Certificate for Indians']
    },
    scholarships: [
      { name: 'DAAD Scholarship', coverage: '€861/month + travel', eligibility: 'Academic excellence' },
      { name: 'Deutschland Stipendium', coverage: '€300/month', eligibility: 'Merit + need' },
      { name: 'Erasmus+', coverage: 'Varies', eligibility: 'EU exchange programs' }
    ],
    timeline: [
      { month: 'Jan-Mar', activity: 'Research programs, language prep' },
      { month: 'Apr-Jun', activity: 'Prepare documents, APS if needed' },
      { month: 'Jul-Sep', activity: 'Apply via uni-assist' },
      { month: 'Oct-Dec', activity: 'Receive admission' },
      { month: 'Jan-Mar', activity: 'Open blocked account (€11,904)' },
      { month: 'Apr-Jun', activity: 'Apply for visa, travel prep' }
    ],
    intakes: [
      {
        name: 'Mar-Apr',
        startMonth: 'May',
        endMonth: 'Apr',
        steps: [
          { month: 'May-Jun', activity: 'Research Universities', details: 'Explore programs, compare universities, prepare for language tests' },
          { month: 'Jun-Jul', activity: 'Take Language Tests', details: 'Complete German/IELTS test, finalize university shortlist' },
          { month: 'Aug', activity: 'Submit Applications', details: 'Apply via uni-assist, prepare documents' },
          { month: 'Oct-Nov', activity: 'Receive Offers', details: 'Get admission letters, accept offer' },
          { month: 'Dec-Jan', activity: 'Open Blocked Account', details: 'Open blocked account (€11,904), apply for visa' },
          { month: 'Mar-Apr', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Sep-Oct',
        startMonth: 'Oct',
        endMonth: 'Jul',
        steps: [
          { month: 'Oct-Nov', activity: 'Research Universities', details: 'Explore programs with winter intake' },
          { month: 'Dec-Jan', activity: 'Take Language Tests', details: 'Complete language tests, finalize shortlist' },
          { month: 'Jan-Mar', activity: 'Submit Applications', details: 'Apply via uni-assist with winter deadlines' },
          { month: 'Apr-May', activity: 'Receive Offers', details: 'Get admission letters, accept offer' },
          { month: 'May', activity: 'Open Blocked Account', details: 'Open blocked account, apply for visa' },
          { month: 'Jun-Jul', activity: 'Travel Prep', details: 'Visa approval, arrange travel' }
        ]
      }
    ]
  },
  'new-zealand': {
    name: 'New Zealand',
    fullName: 'New Zealand',
    flag: '🇳🇿',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4f26352ff923bc62bde468a7177e1dee7222bdf875cbf66f0bd5763f47e843f9.png',
    heroImageMobile: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/b40f199f8f81c61d7b47e7037adff03215303433aa9369314ad44531eecf9b58.png',
    tagline: 'Adventure Meets Education',
    description: 'New Zealand offers a unique combination of quality education, safe environment, stunning natural beauty, and excellent post-study work opportunities.',
    stats: {
      universities: '8',
      internationalStudents: '100K+',
      avgTuition: 'NZD 22,000 - 35,000/year',
      workVisa: '3 Years Post-Study',
      intakes: 'February, July'
    },
    whyStudy: [
      { title: 'Quality Education', description: 'All 8 universities ranked in top 500 globally' },
      { title: 'Work Rights', description: '20 hours/week during term, full-time during breaks' },
      { title: 'Post-Study Work', description: '3-year post-study work visa' },
      { title: 'Safe & Welcoming', description: 'Ranked #2 safest country in the world' },
      { title: 'Natural Beauty', description: 'Stunning landscapes, adventure activities' },
      { title: 'PR Pathway', description: 'Points-based immigration system favors graduates' }
    ],
    topUniversities: [
      { 
        id: 'auckland',
        name: 'University of Auckland', 
        shortName: 'Auckland',
        rank: 68, 
        location: 'Auckland', 
        programs: ['Business', 'Engineering', 'Medicine'],
        tuition: 'NZD 38,000',
        acceptanceRate: '45%',
        avgSalary: 'NZD 75,000',
        studentCount: '46,000',
        intlStudents: '28%',
        duration: '2 years (MS)'
      },
      { 
        id: 'otago',
        name: 'University of Otago', 
        shortName: 'Otago',
        rank: 206, 
        location: 'Dunedin', 
        programs: ['Medicine', 'Dentistry', 'Science'],
        tuition: 'NZD 32,000',
        acceptanceRate: '55%',
        avgSalary: 'NZD 70,000',
        studentCount: '21,000',
        intlStudents: '18%',
        duration: '2 years (MS)'
      },
      { 
        id: 'victoria',
        name: 'Victoria University of Wellington', 
        shortName: 'Victoria',
        rank: 241, 
        location: 'Wellington', 
        programs: ['Law', 'Public Policy', 'Creative Arts'],
        tuition: 'NZD 30,500',
        acceptanceRate: '60%',
        avgSalary: 'NZD 68,000',
        studentCount: '22,000',
        intlStudents: '20%',
        duration: '2 years (MS)'
      },
      { 
        id: 'canterbury',
        name: 'University of Canterbury', 
        shortName: 'Canterbury',
        rank: 256, 
        location: 'Christchurch', 
        programs: ['Engineering', 'Science', 'Arts'],
        tuition: 'NZD 33,500',
        acceptanceRate: '65%',
        avgSalary: 'NZD 65,000',
        studentCount: '17,000',
        intlStudents: '15%',
        duration: '2 years (MS)'
      },
      { 
        id: 'waikato',
        name: 'University of Waikato', 
        shortName: 'Waikato',
        rank: 331, 
        location: 'Hamilton', 
        programs: ['Management', 'Science', 'Engineering'],
        tuition: 'NZD 29,500',
        acceptanceRate: '70%',
        avgSalary: 'NZD 62,000',
        studentCount: '12,500',
        intlStudents: '22%',
        duration: '2 years (MS)'
      },
      { 
        id: 'massey',
        name: 'Massey University', 
        shortName: 'Massey',
        rank: 292, 
        location: 'Palmerston North', 
        programs: ['Agriculture', 'Aviation', 'Veterinary'],
        tuition: 'NZD 28,500',
        acceptanceRate: '72%',
        avgSalary: 'NZD 60,000',
        studentCount: '30,000',
        intlStudents: '16%',
        duration: '2 years (MS)'
      },
    ],
    popularCourses: ['Information Technology', 'Engineering', 'Agriculture', 'Hospitality', 'Film & Creative', 'Environmental Science'],
    costs: { tuitionRange: 'NZD 22,000 - 35,000', livingCost: 'NZD 18,000 - 25,000', totalAnnual: 'NZD 40,000 - 60,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Minimum 60% marks'],
      tests: ['IELTS (6.5+) or PTE (58+)'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume', 'Financial proof']
    },
    scholarships: [
      { name: 'NZ Excellence Awards', coverage: 'NZD 10,000 - 30,000', eligibility: 'Academic merit' },
      { name: 'NZ Scholarship', coverage: 'Full funding', eligibility: 'Developing countries' },
      { name: 'University Scholarships', coverage: '10-50% tuition', eligibility: 'Merit-based' }
    ],
    timeline: [
      { month: 'Mar-May', activity: 'Research programs, IELTS prep' },
      { month: 'Jun-Aug', activity: 'Take IELTS, finalize shortlist' },
      { month: 'Sep-Nov', activity: 'Submit applications' },
      { month: 'Dec-Jan', activity: 'Receive offers' },
      { month: 'Feb-Mar', activity: 'Accept offer, apply for visa' },
      { month: 'Apr-Jun', activity: 'Visa approval, travel prep' }
    ],
    intakes: [
      {
        name: 'Feb-Mar',
        startMonth: 'Feb',
        endMonth: 'Nov',
        steps: [
          { month: 'Feb-Mar', activity: 'Research Universities', details: 'Explore programs, compare universities, prepare for IELTS' },
          { month: 'Apr-May', activity: 'Take IELTS/PTE', details: 'Complete English test, finalize university shortlist' },
          { month: 'May-Jun', activity: 'Submit Applications', details: 'Apply to universities, prepare documents' },
          { month: 'Jul-Aug', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'Sep-Oct', activity: 'Apply for Visa', details: 'Submit student visa application' },
          { month: 'Nov', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Jul',
        startMonth: 'Apr',
        endMonth: 'Jun',
        steps: [
          { month: 'Apr-Jun', activity: 'Research Universities', details: 'Explore programs with July intake' },
          { month: 'Jun-Aug', activity: 'Take IELTS/PTE', details: 'Complete English test, finalize shortlist' },
          { month: 'Sep-Nov', activity: 'Submit Applications', details: 'Apply to universities with July deadlines' },
          { month: 'Dec-Jan', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'Feb-Mar', activity: 'Apply for Visa', details: 'Submit student visa application' },
          { month: 'Apr-Jun', activity: 'Travel Prep', details: 'Visa approval, arrange travel' }
        ]
      }
    ]
  },
  dubai: {
    name: 'Dubai',
    fullName: 'United Arab Emirates',
    flag: '🇦🇪',
    heroImage: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1920&q=80',
    heroImageMobile: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=900&q=75',
    tagline: 'World-Class Education in a Global Hub',
    description: 'Dubai offers a unique blend of world-class education from international branch campuses, tax-free earnings, and unmatched networking opportunities in the heart of the Middle East.',
    stats: {
      universities: '80+',
      internationalStudents: '200K+',
      avgTuition: 'AED 40,000 - 100,000/year',
      workVisa: '2 Years Post-Study',
      intakes: 'September, January, May'
    },
    whyStudy: [
      { title: 'Global Branch Campuses', description: 'Study at NYU, Heriot-Watt, Middlesex & more' },
      { title: 'Tax-Free Earnings', description: 'Work part-time with zero income tax' },
      { title: 'Strategic Location', description: 'Gateway between East and West' },
      { title: 'Safe & Modern', description: 'One of the safest cities globally' },
      { title: 'Diverse Community', description: '200+ nationalities, English widely spoken' },
      { title: 'Career Hub', description: 'Access to Fortune 500 companies & startups' }
    ],
    topUniversities: [
      { 
        id: 'heriot-watt',
        name: 'Heriot-Watt University Dubai', 
        shortName: 'Heriot-Watt',
        rank: 270, 
        location: 'Dubai', 
        programs: ['Engineering', 'Business', 'Psychology'],
        tuition: 'AED 65,000',
        acceptanceRate: '68%',
        avgSalary: 'AED 120,000',
        studentCount: '4,500',
        intlStudents: '90%',
        duration: '2 years (MS)'
      },
      { 
        id: 'middlesex',
        name: 'Middlesex University Dubai', 
        shortName: 'Middlesex',
        rank: 'Top 500', 
        location: 'Dubai', 
        programs: ['Business', 'Law', 'Media'],
        tuition: 'AED 55,000',
        acceptanceRate: '75%',
        avgSalary: 'AED 100,000',
        studentCount: '3,500',
        intlStudents: '95%',
        duration: '2 years (MS)'
      },
      { 
        id: 'bits-pilani',
        name: 'BITS Pilani Dubai', 
        shortName: 'BITS Dubai',
        rank: 'NIRF 28', 
        location: 'Dubai', 
        programs: ['Engineering', 'Computer Science', 'Finance'],
        tuition: 'AED 75,000',
        acceptanceRate: '25%',
        avgSalary: 'AED 130,000',
        studentCount: '2,200',
        intlStudents: '60%',
        duration: '4 years (UG)'
      },
    ],
    popularCourses: ['Business Administration', 'Engineering', 'Hospitality', 'Aviation', 'Finance', 'Marketing'],
    costs: { tuitionRange: 'AED 40,000 - 100,000', livingCost: 'AED 40,000 - 70,000', totalAnnual: 'AED 80,000 - 170,000' },
    requirements: {
      academic: ['High school diploma for UG', 'Bachelor\'s for Masters'],
      tests: ['IELTS (6.0+) or PTE (50+)', 'Some programs accept without IELTS'],
      documents: ['Transcripts', 'Passport copy', 'Emirates ID (if resident)', 'SOP', 'LORs']
    },
    scholarships: [
      { name: 'Merit Scholarships', coverage: '25-100% tuition', eligibility: 'Academic excellence' },
      { name: 'Sports Scholarships', coverage: '50-100% tuition', eligibility: 'National/International athletes' },
      { name: 'University-specific Awards', coverage: '10-50% tuition', eligibility: 'Varies by institution' }
    ],
    timeline: [
      { month: 'Jan-Mar', activity: 'Research universities, prepare documents' },
      { month: 'Apr-May', activity: 'Submit applications for September intake' },
      { month: 'Jun-Jul', activity: 'Receive offers, accept and pay deposit' },
      { month: 'Aug', activity: 'Apply for student visa' },
      { month: 'Sep', activity: 'Arrive in Dubai, orientation begins' }
    ],
    intakes: [
      {
        name: 'Feb-Mar',
        startMonth: 'Jun',
        endMonth: 'Jan',
        steps: [
          { month: 'Jun', activity: 'Research Universities', details: 'Explore programs, compare universities in Dubai' },
          { month: 'Jul-Aug', activity: 'Prepare Documents', details: 'Prepare all required documents, transcripts' },
          { month: 'Sep-Oct', activity: 'Submit Applications', details: 'Apply to universities, pay application fees' },
          { month: 'Dec', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'Jan', activity: 'Visa & Travel', details: 'Apply for student visa, arrange accommodation' }
        ]
      },
      {
        name: 'May-Jun',
        startMonth: 'Oct',
        endMonth: 'May',
        steps: [
          { month: 'Oct-Nov', activity: 'Research Universities', details: 'Explore programs with May-Jun intake' },
          { month: 'Dec-Jan', activity: 'Prepare Documents', details: 'Prepare documents, finalize shortlist' },
          { month: 'Feb-Mar', activity: 'Submit Applications', details: 'Apply to universities' },
          { month: 'Apr', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'May', activity: 'Visa & Travel', details: 'Apply for visa, arrange travel' }
        ]
      },
      {
        name: 'Sep',
        startMonth: 'Jan',
        endMonth: 'Sep',
        steps: [
          { month: 'Jan-Mar', activity: 'Research Universities', details: 'Explore programs with September intake' },
          { month: 'Apr-May', activity: 'Prepare Documents', details: 'Prepare documents, finalize shortlist' },
          { month: 'Jun-Jul', activity: 'Submit Applications', details: 'Apply to universities' },
          { month: 'Aug', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'Sep', activity: 'Visa & Travel', details: 'Apply for visa, arrange travel' }
        ]
      }
    ]
  },
  france: {
    name: 'France',
    fullName: 'France',
    flag: '🇫🇷',
    heroImage: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
    heroImageMobile: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80',
    tagline: 'Excellence in Art, Culture & Innovation',
    description: 'France offers world-renowned education in business, fashion, culinary arts, and engineering with affordable tuition and a rich cultural experience in the heart of Europe.',
    stats: {
      universities: '3500+',
      internationalStudents: '370K+',
      avgTuition: '€2,770 - €20,000/year',
      workVisa: '1 Year APS (Job Seeker)',
      intakes: 'September, January'
    },
    whyStudy: [
      { title: 'Affordable Education', description: 'Public universities charge very low tuition fees, even for international students' },
      { title: 'World-Class Business Schools', description: 'Home to top-ranked business schools like HEC Paris, INSEAD, and ESSEC' },
      { title: 'Rich Culture & History', description: 'Experience art, cuisine, fashion, and history in one of the world\'s most beautiful countries' },
      { title: 'Strategic Location', description: 'Gateway to Europe with easy access to other EU countries' },
      { title: 'Research Excellence', description: 'Strong emphasis on research and innovation across all fields' },
      { title: 'Work Opportunities', description: '12-month APS (Autorisation Provisoire de Séjour) post-study work permit for Master\'s graduates + growing tech startup ecosystem' }
    ],
    topUniversities: [
      { 
        id: 'psl',
        name: 'Paris Sciences et Lettres (PSL)', 
        shortName: 'PSL',
        rank: 24, 
        location: 'Paris', 
        programs: ['Science', 'Arts', 'Social Sciences'],
        tuition: '€3,770',
        acceptanceRate: '10%',
        avgSalary: '€50,000',
        studentCount: '17,000',
        intlStudents: '30%',
        duration: '2 years (MS)'
      },
      { 
        id: 'polytechnique',
        name: 'École Polytechnique', 
        shortName: 'Polytechnique',
        rank: 38, 
        location: 'Paris', 
        programs: ['Engineering', 'Science', 'Economics'],
        tuition: '€15,500',
        acceptanceRate: '5%',
        avgSalary: '€65,000',
        studentCount: '3,000',
        intlStudents: '40%',
        duration: '2 years (MS)'
      },
      { 
        id: 'sorbonne',
        name: 'Sorbonne University', 
        shortName: 'Sorbonne',
        rank: 43, 
        location: 'Paris', 
        programs: ['Humanities', 'Medicine', 'Science'],
        tuition: '€2,770',
        acceptanceRate: '25%',
        avgSalary: '€45,000',
        studentCount: '55,000',
        intlStudents: '20%',
        duration: '2 years (MS)'
      },
      { 
        id: 'hec',
        name: 'HEC Paris', 
        shortName: 'HEC',
        rank: 'MBA #5 Global (QS 2027)', 
        location: 'Paris', 
        programs: ['MBA', 'Finance', 'Management'],
        tuition: '€49,500',
        acceptanceRate: '8%',
        avgSalary: '€95,000',
        studentCount: '4,500',
        intlStudents: '70%',
        duration: '16 months (MBA)'
      },
      { 
        id: 'insead',
        name: 'INSEAD', 
        shortName: 'INSEAD',
        rank: 'MBA #4 Global', 
        location: 'Fontainebleau', 
        programs: ['MBA', 'Executive MBA', 'PhD'],
        tuition: '€89,000',
        acceptanceRate: '15%',
        avgSalary: '€110,000',
        studentCount: '1,300',
        intlStudents: '95%',
        duration: '10 months (MBA)'
      },
      { 
        id: 'sciencespo',
        name: 'Sciences Po', 
        shortName: 'Sciences Po',
        rank: 'Top 5 Political Science', 
        location: 'Paris', 
        programs: ['Political Science', 'International Relations', 'Public Policy'],
        tuition: '€14,500',
        acceptanceRate: '15%',
        avgSalary: '€55,000',
        studentCount: '14,000',
        intlStudents: '50%',
        duration: '2 years (MS)'
      },
    ],
    popularCourses: ['Business & Management', 'Fashion & Design', 'Culinary Arts', 'Engineering', 'Political Science', 'Luxury Brand Management'],
    costs: { tuitionRange: '€2,770 - €20,000', livingCost: '€10,000 - €15,000', totalAnnual: '€12,770 - €35,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Strong academic record'],
      tests: ['French (B2) for French programs', 'English (IELTS 6.5+) for English programs'],
      documents: ['Transcripts', 'Motivation Letter', 'LORs', 'CV', 'Campus France registration']
    },
    scholarships: [
      { name: 'Eiffel Excellence Scholarship', coverage: '€1,181/month + benefits', eligibility: 'International students, merit-based' },
      { name: 'Charpak Scholarship', coverage: '€500-700/month', eligibility: 'Indian students' },
      { name: 'Regional Council Scholarships', coverage: 'Varies', eligibility: 'Based on region and program' }
    ],
    timeline: [
      { month: 'Oct-Dec', activity: 'Research programs, French/English test prep' },
      { month: 'Jan-Mar', activity: 'Campus France registration, submit applications' },
      { month: 'Apr-May', activity: 'Receive admission decisions' },
      { month: 'Jun-Jul', activity: 'Accept offer, begin visa process' },
      { month: 'Aug', activity: 'Visa interview, pre-departure prep' },
      { month: 'Sep', activity: 'Arrive in France, begin studies' }
    ],
    intakes: [
      {
        name: 'Feb-Apr',
        startMonth: 'Jul',
        endMonth: 'Apr',
        steps: [
          { month: 'Jul-Aug', activity: 'Research Universities', details: 'Explore programs, compare universities, prepare for language tests' },
          { month: 'Sep-Oct', activity: 'Take Language Tests', details: 'Complete French/IELTS test, finalize university shortlist' },
          { month: 'Nov-Dec', activity: 'Submit Applications', details: 'Campus France registration, submit applications' },
          { month: 'Jan-Feb', activity: 'Receive Offers', details: 'Get admission decisions, accept offer' },
          { month: 'Mar', activity: 'Apply for Visa', details: 'Submit visa application' },
          { month: 'Apr', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Sep-Oct',
        startMonth: 'Oct',
        endMonth: 'Sep',
        steps: [
          { month: 'Oct-Dec', activity: 'Research Universities', details: 'Explore programs with September intake' },
          { month: 'Jan-Mar', activity: 'Take Language Tests', details: 'Complete language tests, finalize shortlist' },
          { month: 'Apr-May', activity: 'Submit Applications', details: 'Campus France registration, submit applications' },
          { month: 'Jun-Jul', activity: 'Receive Offers', details: 'Get admission decisions, accept offer' },
          { month: 'Aug', activity: 'Apply for Visa', details: 'Submit visa application' },
          { month: 'Sep', activity: 'Travel Prep', details: 'Visa approval, arrange travel' }
        ]
      }
    ]
  },
  singapore: {
    name: 'Singapore',
    fullName: 'Singapore',
    flag: '🇸🇬',
    heroImage: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=80',
    heroImageMobile: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80',
    tagline: 'Asia\'s Premier Education Hub',
    description: 'Singapore offers world-class education with a unique blend of Eastern and Western perspectives, excellent infrastructure, and strong connections to Asian markets.',
    stats: {
      universities: '34',
      internationalStudents: '75K+',
      avgTuition: 'SGD 30,000 - 60,000/year',
      workVisa: '1 Year Post-Study Work',
      intakes: 'August, January'
    },
    whyStudy: [
      { title: 'Top Asian Universities', description: 'Home to NUS and NTU, consistently ranked among Asia\'s best' },
      { title: 'Multicultural Environment', description: 'Diverse, English-speaking society with Asian roots' },
      { title: 'Business Hub', description: 'Gateway to Asian markets with strong corporate presence' },
      { title: 'Safe & Clean', description: 'One of the world\'s safest and cleanest cities' },
      { title: 'Tech Innovation', description: 'Growing fintech and startup ecosystem' },
      { title: 'Quality of Life', description: 'Excellent infrastructure, healthcare, and public transport' }
    ],
    topUniversities: [
      { 
        id: 'nus',
        name: 'National University of Singapore', 
        shortName: 'NUS',
        rank: 8, 
        location: 'Singapore', 
        programs: ['Computer Science', 'Business', 'Engineering'],
        tuition: 'SGD 45,000',
        acceptanceRate: '20%',
        avgSalary: 'SGD 80,000',
        studentCount: '42,000',
        intlStudents: '35%',
        duration: '1.5 years (MS)'
      },
      { 
        id: 'ntu',
        name: 'Nanyang Technological University', 
        shortName: 'NTU',
        rank: 15, 
        location: 'Singapore', 
        programs: ['Engineering', 'Business', 'Communication'],
        tuition: 'SGD 42,000',
        acceptanceRate: '25%',
        avgSalary: 'SGD 75,000',
        studentCount: '33,000',
        intlStudents: '30%',
        duration: '1.5 years (MS)'
      },
      { 
        id: 'smu',
        name: 'Singapore Management University', 
        shortName: 'SMU',
        rank: 'Top 50 Business', 
        location: 'Singapore', 
        programs: ['Business', 'Finance', 'Law'],
        tuition: 'SGD 50,000',
        acceptanceRate: '30%',
        avgSalary: 'SGD 70,000',
        studentCount: '10,000',
        intlStudents: '40%',
        duration: '1 year (MS)'
      },
      { 
        id: 'sutd',
        name: 'Singapore University of Technology and Design', 
        shortName: 'SUTD',
        rank: 'Top 10 Design', 
        location: 'Singapore', 
        programs: ['Design', 'Engineering', 'Architecture'],
        tuition: 'SGD 38,000',
        acceptanceRate: '15%',
        avgSalary: 'SGD 72,000',
        studentCount: '2,200',
        intlStudents: '35%',
        duration: '1.5 years (MS)'
      },
      { 
        id: 'insead-sg',
        name: 'INSEAD Singapore', 
        shortName: 'INSEAD',
        rank: 'MBA #4 Global', 
        location: 'Singapore', 
        programs: ['MBA', 'Executive MBA'],
        tuition: 'SGD 120,000',
        acceptanceRate: '15%',
        avgSalary: 'SGD 150,000',
        studentCount: '1,000',
        intlStudents: '95%',
        duration: '10 months (MBA)'
      },
      { 
        id: 'sit',
        name: 'Singapore Institute of Technology', 
        shortName: 'SIT',
        rank: 'Applied Learning', 
        location: 'Singapore', 
        programs: ['Engineering', 'IT', 'Healthcare'],
        tuition: 'SGD 28,000',
        acceptanceRate: '40%',
        avgSalary: 'SGD 55,000',
        studentCount: '8,000',
        intlStudents: '20%',
        duration: '1.5 years (MS)'
      },
    ],
    popularCourses: ['Computer Science & AI', 'Business & Finance', 'Engineering', 'Data Science', 'Hospitality', 'Biomedical Sciences'],
    costs: { tuitionRange: 'SGD 30,000 - 60,000', livingCost: 'SGD 12,000 - 20,000', totalAnnual: 'SGD 42,000 - 80,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Strong academic record (typically 3.0+ GPA)'],
      tests: ['IELTS (6.5+) or TOEFL (90+)', 'GRE/GMAT for some programs'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume', 'Portfolio (for design)']
    },
    scholarships: [
      { name: 'Singapore Government Scholarships', coverage: 'Full tuition + allowance', eligibility: 'Merit-based, service bond' },
      { name: 'University Merit Scholarships', coverage: 'SGD 5,000 - 20,000', eligibility: 'Academic excellence' },
      { name: 'ASEAN Scholarships', coverage: 'Full tuition', eligibility: 'ASEAN citizens' }
    ],
    timeline: [
      { month: 'Aug-Oct', activity: 'Research programs, prepare for tests' },
      { month: 'Nov-Jan', activity: 'Submit applications' },
      { month: 'Feb-Apr', activity: 'Interviews, admission decisions' },
      { month: 'May-Jun', activity: 'Accept offer, apply for student pass' },
      { month: 'Jul', activity: 'Pre-departure preparation' },
      { month: 'Aug', activity: 'Arrive in Singapore, orientation' }
    ],
    intakes: [
      {
        name: 'Feb-Mar',
        startMonth: 'Jul',
        endMonth: 'Jan',
        steps: [
          { month: 'Jul-Aug', activity: 'Research Universities', details: 'Explore programs, compare universities' },
          { month: 'Aug-Sep', activity: 'Prepare Documents', details: 'Prepare documents, take IELTS/TOEFL' },
          { month: 'Sep-Oct', activity: 'Submit Applications', details: 'Apply to universities, pay fees' },
          { month: 'Oct-Nov', activity: 'Receive Offers', details: 'Get admission decisions, accept offer' },
          { month: 'Dec', activity: 'Apply for Student Pass', details: 'Submit student pass application' },
          { month: 'Jan', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Aug',
        startMonth: 'Aug',
        endMonth: 'Aug',
        steps: [
          { month: 'Aug-Oct', activity: 'Research Universities', details: 'Explore programs with August intake' },
          { month: 'Nov-Jan', activity: 'Prepare Documents', details: 'Prepare documents, take tests' },
          { month: 'Feb-Apr', activity: 'Submit Applications', details: 'Apply to universities' },
          { month: 'May-Jun', activity: 'Receive Offers', details: 'Get admission decisions, accept offer' },
          { month: 'Jul', activity: 'Apply for Student Pass', details: 'Submit student pass application' },
          { month: 'Aug', activity: 'Travel Prep', details: 'Visa approval, arrange travel' }
        ]
      }
    ]
  },
  malaysia: {
    name: 'Malaysia',
    fullName: 'Malaysia',
    flag: '🇲🇾',
    heroImage: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=1920&q=80',
    heroImageMobile: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&q=80',
    tagline: 'Quality Education at Affordable Costs',
    description: 'Malaysia offers quality international education at a fraction of Western costs, with numerous branch campuses of UK and Australian universities and a multicultural environment.',
    stats: {
      universities: '170+',
      internationalStudents: '130K+',
      avgTuition: 'MYR 15,000 - 50,000/year',
      workVisa: 'Part-time Work Allowed',
      intakes: 'February, July, September'
    },
    whyStudy: [
      { title: 'Affordable Tuition', description: 'Quality education at 50-70% lower cost than Western countries' },
      { title: 'International Campuses', description: 'Branch campuses of top UK and Australian universities' },
      { title: 'English Medium', description: 'Most programs taught in English in a multicultural setting' },
      { title: 'Strategic Location', description: 'Gateway to Southeast Asia with easy travel access' },
      { title: 'Cultural Diversity', description: 'Experience Malay, Chinese, and Indian cultures together' },
      { title: 'Growing Economy', description: 'Expanding industries with internship opportunities' }
    ],
    topUniversities: [
      { 
        id: 'um',
        name: 'Universiti Malaya', 
        shortName: 'UM',
        rank: 65, 
        location: 'Kuala Lumpur', 
        programs: ['Medicine', 'Engineering', 'Business'],
        tuition: 'MYR 25,000',
        acceptanceRate: '40%',
        avgSalary: 'MYR 50,000',
        studentCount: '28,000',
        intlStudents: '20%',
        duration: '2 years (MS)'
      },
      { 
        id: 'nottingham-my',
        name: 'University of Nottingham Malaysia', 
        shortName: 'Nottingham MY',
        rank: 100, 
        location: 'Semenyih', 
        programs: ['Engineering', 'Business', 'Science'],
        tuition: 'MYR 45,000',
        acceptanceRate: '60%',
        avgSalary: 'MYR 55,000',
        studentCount: '5,000',
        intlStudents: '50%',
        duration: '1 year (MS)'
      },
      { 
        id: 'monash-my',
        name: 'Monash University Malaysia', 
        shortName: 'Monash MY',
        rank: 42, 
        location: 'Subang Jaya', 
        programs: ['Business', 'Engineering', 'Pharmacy'],
        tuition: 'MYR 48,000',
        acceptanceRate: '55%',
        avgSalary: 'MYR 58,000',
        studentCount: '8,500',
        intlStudents: '35%',
        duration: '1.5 years (MS)'
      },
      { 
        id: 'taylors',
        name: 'Taylor\'s University', 
        shortName: 'Taylor\'s',
        rank: 284, 
        location: 'Subang Jaya', 
        programs: ['Hospitality', 'Business', 'Design'],
        tuition: 'MYR 40,000',
        acceptanceRate: '70%',
        avgSalary: 'MYR 45,000',
        studentCount: '10,000',
        intlStudents: '30%',
        duration: '1.5 years (MS)'
      },
      { 
        id: 'ukm',
        name: 'Universiti Kebangsaan Malaysia', 
        shortName: 'UKM',
        rank: 144, 
        location: 'Bangi', 
        programs: ['Science', 'Medicine', 'Engineering'],
        tuition: 'MYR 20,000',
        acceptanceRate: '45%',
        avgSalary: 'MYR 48,000',
        studentCount: '26,000',
        intlStudents: '15%',
        duration: '2 years (MS)'
      },
      { 
        id: 'curtin-my',
        name: 'Curtin University Malaysia', 
        shortName: 'Curtin MY',
        rank: 183, 
        location: 'Miri', 
        programs: ['Engineering', 'Business', 'Science'],
        tuition: 'MYR 35,000',
        acceptanceRate: '65%',
        avgSalary: 'MYR 52,000',
        studentCount: '4,500',
        intlStudents: '40%',
        duration: '1.5 years (MS)'
      },
    ],
    popularCourses: ['Business & Management', 'Engineering', 'IT & Computer Science', 'Hospitality & Tourism', 'Medicine', 'Accounting'],
    costs: { tuitionRange: 'MYR 15,000 - 50,000', livingCost: 'MYR 12,000 - 18,000', totalAnnual: 'MYR 27,000 - 68,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Good academic record'],
      tests: ['IELTS (6.0+) or equivalent', 'Some universities accept without IELTS'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume', 'Health declaration']
    },
    scholarships: [
      { name: 'Malaysian Government Scholarships', coverage: 'Full tuition + allowance', eligibility: 'Merit-based' },
      { name: 'University Merit Awards', coverage: 'MYR 5,000 - 20,000', eligibility: 'Academic excellence' },
      { name: 'Branch Campus Scholarships', coverage: '25-50% tuition', eligibility: 'Varies by institution' }
    ],
    timeline: [
      { month: 'Oct-Dec', activity: 'Research programs, prepare documents' },
      { month: 'Jan-Mar', activity: 'Submit applications for July intake' },
      { month: 'Apr-May', activity: 'Receive offers, accept admission' },
      { month: 'Jun', activity: 'Apply for student visa' },
      { month: 'Jul', activity: 'Arrive in Malaysia, begin studies' }
    ],
    intakes: [
      {
        name: 'Feb-Mar',
        startMonth: 'Jun',
        endMonth: 'Jan',
        steps: [
          { month: 'Jun-Jul', activity: 'Research Universities', details: 'Explore programs, compare universities' },
          { month: 'Jul-Aug', activity: 'Prepare Documents', details: 'Prepare documents, take IELTS if needed' },
          { month: 'Sep-Oct', activity: 'Submit Applications', details: 'Apply to universities, pay fees' },
          { month: 'Nov-Dec', activity: 'Receive Offers', details: 'Get admission decisions, accept offer' },
          { month: 'Jan', activity: 'Visa & Travel', details: 'Apply for student visa, arrange accommodation' }
        ]
      },
      {
        name: 'Jul',
        startMonth: 'Oct',
        endMonth: 'Jul',
        steps: [
          { month: 'Oct-Dec', activity: 'Research Universities', details: 'Explore programs with July intake' },
          { month: 'Jan-Mar', activity: 'Prepare Documents', details: 'Prepare documents, take tests' },
          { month: 'Apr-May', activity: 'Submit Applications', details: 'Apply to universities' },
          { month: 'May-Jun', activity: 'Receive Offers', details: 'Get admission decisions, accept offer' },
          { month: 'Jul', activity: 'Visa & Travel', details: 'Apply for visa, arrange travel' }
        ]
      }
    ]
  },
  ireland: {
    name: 'Ireland',
    fullName: 'Ireland',
    flag: '🇮🇪',
    heroImage: 'https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=1920&q=80',
    heroImageMobile: 'https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=800&q=80',
    tagline: 'Europe\'s Tech & Pharma Capital',
    description: 'Ireland offers excellent education with strong links to multinational tech and pharmaceutical companies, generous post-study work rights, and a welcoming English-speaking environment.',
    stats: {
      universities: '34',
      internationalStudents: '35K+',
      avgTuition: '€10,000 - €25,000/year',
      workVisa: '2 Year Stay Back',
      intakes: 'September, January'
    },
    whyStudy: [
      { title: 'Tech Hub of Europe', description: 'European headquarters of Google, Facebook, Apple, and Microsoft' },
      { title: 'Pharma & Biotech', description: 'Major hub for pharmaceutical and biotechnology companies' },
      { title: 'Stay Back Option', description: 'Up to 2 years post-study work visa for Master\'s graduates' },
      { title: 'English Speaking', description: 'Native English-speaking country with friendly locals' },
      { title: 'EU Access', description: 'Gateway to Europe with easy travel and career opportunities' },
      { title: 'Rich Culture', description: 'Vibrant music, literature, and cultural heritage' }
    ],
    topUniversities: [
      { 
        id: 'tcd',
        name: 'Trinity College Dublin', 
        shortName: 'TCD',
        rank: 81, 
        location: 'Dublin', 
        programs: ['Computer Science', 'Business', 'Law'],
        tuition: '€18,000',
        acceptanceRate: '30%',
        avgSalary: '€55,000',
        studentCount: '18,000',
        intlStudents: '35%',
        duration: '1 year (MS)'
      },
      { 
        id: 'ucd',
        name: 'University College Dublin', 
        shortName: 'UCD',
        rank: 126, 
        location: 'Dublin', 
        programs: ['Business', 'Engineering', 'Agriculture'],
        tuition: '€20,000',
        acceptanceRate: '40%',
        avgSalary: '€50,000',
        studentCount: '35,000',
        intlStudents: '30%',
        duration: '1 year (MS)'
      },
      { 
        id: 'nuig',
        name: 'University of Galway', 
        shortName: 'UoG',
        rank: 273, 
        location: 'Galway', 
        programs: ['Medicine', 'Engineering', 'Arts'],
        tuition: '€15,000',
        acceptanceRate: '50%',
        avgSalary: '€45,000',
        studentCount: '19,000',
        intlStudents: '25%',
        duration: '1 year (MS)'
      },
      { 
        id: 'ucc',
        name: 'University College Cork', 
        shortName: 'UCC',
        rank: 292, 
        location: 'Cork', 
        programs: ['Pharmacy', 'Food Science', 'Business'],
        tuition: '€16,000',
        acceptanceRate: '45%',
        avgSalary: '€47,000',
        studentCount: '22,000',
        intlStudents: '22%',
        duration: '1 year (MS)'
      },
      { 
        id: 'dcu',
        name: 'Dublin City University', 
        shortName: 'DCU',
        rank: 436, 
        location: 'Dublin', 
        programs: ['Computer Science', 'Engineering', 'Business'],
        tuition: '€14,000',
        acceptanceRate: '55%',
        avgSalary: '€48,000',
        studentCount: '17,000',
        intlStudents: '28%',
        duration: '1 year (MS)'
      },
      { 
        id: 'ul',
        name: 'University of Limerick', 
        shortName: 'UL',
        rank: 426, 
        location: 'Limerick', 
        programs: ['Engineering', 'Business', 'Sports Science'],
        tuition: '€13,500',
        acceptanceRate: '55%',
        avgSalary: '€45,000',
        studentCount: '16,000',
        intlStudents: '20%',
        duration: '1 year (MS)'
      },
    ],
    popularCourses: ['Computer Science & IT', 'Data Analytics', 'Pharmaceutical Science', 'Business & Finance', 'Biotechnology', 'Digital Marketing'],
    costs: { tuitionRange: '€10,000 - €25,000', livingCost: '€10,000 - €15,000', totalAnnual: '€20,000 - €40,000' },
    requirements: {
      academic: ['Bachelor\'s degree for Masters', 'Good academic record (typically 2:1 equivalent)'],
      tests: ['IELTS (6.5+) or equivalent', 'No GRE/GMAT for most programs'],
      documents: ['Transcripts', 'SOP', 'LORs', 'Resume', 'Proof of funds']
    },
    scholarships: [
      { name: 'Government of Ireland Scholarships', coverage: '€10,000 + tuition', eligibility: 'Merit-based, all nationalities' },
      { name: 'University Scholarships', coverage: '€3,000 - €10,000', eligibility: 'Academic excellence' },
      { name: 'Science Foundation Ireland', coverage: 'Full funding', eligibility: 'STEM research students' }
    ],
    timeline: [
      { month: 'Sep-Nov', activity: 'Research programs, prepare for IELTS' },
      { month: 'Dec-Feb', activity: 'Submit applications' },
      { month: 'Mar-Apr', activity: 'Receive admission decisions' },
      { month: 'May-Jun', activity: 'Accept offer, pay deposit' },
      { month: 'Jul-Aug', activity: 'Apply for visa, pre-departure prep' },
      { month: 'Sep', activity: 'Arrive in Ireland, orientation begins' }
    ],
    intakes: [
      {
        name: 'Jan',
        startMonth: 'Feb',
        endMonth: 'Jan',
        steps: [
          { month: 'Feb-Mar', activity: 'Research Universities', details: 'Explore programs, compare universities, prepare for IELTS' },
          { month: 'Mar', activity: 'Take IELTS', details: 'Complete IELTS test, finalize university shortlist' },
          { month: 'Apr-Jun', activity: 'Submit Applications', details: 'Apply to universities, prepare documents' },
          { month: 'Jul-Sep', activity: 'Receive Offers', details: 'Get admission decisions, accept offer' },
          { month: 'Oct-Nov', activity: 'Apply for Visa', details: 'Submit student visa application' },
          { month: 'Jan', activity: 'Travel Prep', details: 'Visa approval, book flights, arrange accommodation' }
        ]
      },
      {
        name: 'Sep',
        startMonth: 'Sep',
        endMonth: 'Sep',
        steps: [
          { month: 'Sep-Nov', activity: 'Research Universities', details: 'Explore programs with September intake' },
          { month: 'Dec-Feb', activity: 'Take IELTS', details: 'Complete IELTS test, finalize shortlist' },
          { month: 'Mar-Apr', activity: 'Submit Applications', details: 'Apply to universities with September start' },
          { month: 'May-Jun', activity: 'Receive Offers', details: 'Get offer letters, accept admission' },
          { month: 'Jun-Jul', activity: 'Apply for Visa', details: 'Submit student visa application' },
          { month: 'Sep', activity: 'Travel Prep', details: 'Visa approval, arrange travel' }
        ]
      }
    ]
  }
};

export default destinationData;
export { destinationData };

